// countryEnrichment.js
// Puts the visitor's country on every Amplitude event, including the very
// first page view and session start.
//
// Since IP tracking was turned off (privacy rules), Amplitude can no longer
// work out location itself. The country comes from Netlify's edge header via
// /api/geo instead (see netlify/functions/geo.js). It used to be attached
// with an Identify after the fetch returned, which only tagged events sent
// *after* that point. The automatic page view fires as soon as Amplitude
// initializes, so it almost always missed the country. In baseline mode
// every page load is a brand-new anonymous device, so those views showed up
// with no country at all (about half of all views).
//
// This plugin is registered before amplitude.init, so the SDK runs it ahead
// of its own page-view and session tracking. Each event waits for the one
// lookup per page load (capped by a timeout) and then gets the country.

// Long enough for a normal edge function round trip, short enough that a
// slow or failing lookup can't hold analytics back for long. Events still
// go out without a country if it runs out.
export const GEO_TIMEOUT_MS = 1500;

const IDENTIFY_EVENT_TYPES = new Set(['$identify', '$groupidentify']);

/**
 * Look up the visitor's 2-letter country code. Never rejects: resolves to
 * null on any failure or after the timeout.
 * @param {typeof fetch} fetchImpl
 * @param {number} timeoutMs
 * @returns {Promise<string|null>}
 */
export function fetchCountry(
  fetchImpl = typeof window !== 'undefined' && window.fetch ? window.fetch.bind(window) : undefined,
  timeoutMs = GEO_TIMEOUT_MS,
) {
  if (typeof fetchImpl !== 'function') return Promise.resolve(null);

  const lookup = Promise.resolve()
    .then(() => fetchImpl('/api/geo'))
    .then((res) => (res && res.ok ? res.json() : null))
    .then((data) => normalizeCountry(data && data.country))
    .catch(() => null);

  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([lookup, timeout]).finally(() => clearTimeout(timer));
}

/**
 * @param {unknown} value
 * @returns {string|null} an upper-case 2-letter code, or null
 */
export function normalizeCountry(value) {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
}

/**
 * Amplitude enrichment plugin that sets the country on each event.
 *
 * - event.country fills Amplitude's built-in Country property.
 * - user_properties.country keeps the custom `country` user property that
 *   existing charts use. Identify events are left alone: their
 *   user_properties hold $set/$unset operations, not plain values.
 *
 * @param {Promise<string|null>} countryPromise
 */
export function createCountryPlugin(countryPromise) {
  return {
    name: 'topamine-country-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      let country = null;
      try {
        country = await countryPromise;
      } catch (e) {
        country = null;
      }
      if (!country || !event) return event;

      if (!event.country) event.country = country;
      if (!IDENTIFY_EVENT_TYPES.has(event.event_type)) {
        event.user_properties = { ...(event.user_properties || {}), country };
      }
      return event;
    },
  };
}
